import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useOrder, useExportPersonalizationCSV, useReprintLabel, useOrderTracking, useSetOrderTracking } from "./api/orderHooks";
import { PageContainer } from "@/components/layout/PageContainer";
import { DetailView } from "@/components/layout/DetailView";
import { SectionGroup } from "@/components/layout/SectionGroup";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  ArrowLeft,
  MoreHorizontal,
  Download,
  Loader2,
  Package,
  Copy,
  Check,
  Printer,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import { ShopeeShippingSheet } from "./components/ShopeeShippingSheet";
import { OrderStatusBadge } from "./components/OrderStatusBadge";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SHIPPED_STATUSES = ["SHIPPED", "TO_CONFIRM_RECEIVE", "COMPLETED"];
const CANCELLED_STATUSES = ["CANCELLED", "IN_CANCEL"];

function isWithinCancellationWindow(creation?: string): boolean {
  if (!creation) return false;
  const created = new Date(creation).getTime();
  const now = Date.now();
  return now - created < 60 * 60 * 1000; // 1 hour
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useOrder(id || "");
  const { exportCSV, isExporting } = useExportPersonalizationCSV();

  // Shopee shipping state
  const [shippingSheetOpen, setShippingSheetOpen] = useState(false);
  const [trackingCopied, setTrackingCopied] = useState(false);
  const [reprintOpen, setReprintOpen] = useState(false);
  const reprintMutation = useReprintLabel(id || "");

  // Tracking state (non-marketplace orders)
  const trackingQuery = useOrderTracking(id || "");
  const setTrackingMutation = useSetOrderTracking(id || "");
  const [trackingInput, setTrackingInput] = useState("");
  const [showTrackingForm, setShowTrackingForm] = useState(false);

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading order...</div>
        </div>
      </PageContainer>
    );
  }

  if (error || !data) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center h-64">
          <div className="text-destructive">Error loading order</div>
        </div>
      </PageContainer>
    );
  }

  // Shopee order detection
  const isShopeeOrder = !!data.shopee_order_sn;
  const shopeeStatus = data.shopee_order_status || "";
  const isReadyToShip = shopeeStatus === "READY_TO_SHIP";
  const isShipped = SHIPPED_STATUSES.includes(shopeeStatus);
  const isCancelled = CANCELLED_STATUSES.includes(shopeeStatus);
  const inCancellationWindow = isWithinCancellationWindow(data.creation);

  // Reprint handler — downloads PDF in chosen format
  const handleReprint = (format: string) => {
    reprintMutation.mutate(format, {
      onSuccess: (blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `shopee-label-${data.shopee_order_sn}.pdf`;
        link.click();
        URL.revokeObjectURL(url);
        try { localStorage.setItem("shopee_label_format", format); } catch { /* noop */ }
        setReprintOpen(false);
        toast.success("Label downloaded");
      },
    });
  };

  const handleCopyTracking = () => {
    if (!data.shopee_tracking_no) return;
    navigator.clipboard.writeText(data.shopee_tracking_no).then(() => {
      setTrackingCopied(true);
      toast.success("Tracking number copied");
      setTimeout(() => setTrackingCopied(false), 2000);
    });
  };

  return (
    <PageContainer>
      <DetailView
        title={`Order ${data.name}`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate("/orders")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {/* Shopee Ship Order button */}
            {isShopeeOrder && isReadyToShip && !isCancelled && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        onClick={() => setShippingSheetOpen(true)}
                        disabled={inCancellationWindow}
                        className="bg-sky-500 hover:bg-sky-600 text-white"
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Ship Order
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {inCancellationWindow && (
                    <TooltipContent>
                      <p>Available after buyer cancellation window (1 hour)</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Shopee Reprint Label button with format picker */}
            {isShopeeOrder && isShipped && (
              <Popover open={reprintOpen} onOpenChange={setReprintOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={reprintMutation.isPending}
                  >
                    {reprintMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Printer className="w-4 h-4 mr-2" />
                    )}
                    Reprint Label
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-48 p-1">
                  <button
                    onClick={() => handleReprint("THERMAL_AIR_WAYBILL")}
                    className="w-full px-3 py-2 text-left text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    Thermal (100x150mm)
                  </button>
                  <button
                    onClick={() => handleReprint("NORMAL_AIR_WAYBILL")}
                    className="w-full px-3 py-2 text-left text-sm rounded-md hover:bg-muted transition-colors"
                  >
                    A4 Standard
                  </button>
                </PopoverContent>
              </Popover>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => exportCSV(id)}
                  disabled={isExporting}
                >
                  {isExporting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export Personalization CSV
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      >
        <SectionGroup title="Order Information">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Order ID
              </label>
              <p className="mt-1">{data.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Customer
              </label>
              <p className="mt-1">{data.customer_name || data.customer}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Transaction Date
              </label>
              <p className="mt-1">
                {data.transaction_date
                  ? new Date(data.transaction_date).toLocaleDateString()
                  : "-"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Delivery Date
              </label>
              <p className="mt-1">
                {data.delivery_date
                  ? new Date(data.delivery_date).toLocaleDateString()
                  : "-"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Status
              </label>
              <div className="mt-1">
                <OrderStatusBadge
                  status={data.status || ""}
                  perDelivered={data.per_delivered}
                  marketplaceStatus={data.shopee_order_status || data.lazada_order_status}
                />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">
                Grand Total
              </label>
              <p className="mt-1 font-semibold">
                {data.grand_total
                  ? `${data.currency || "$"}${data.grand_total.toFixed(2)}`
                  : "-"}
              </p>
            </div>
          </div>
        </SectionGroup>

        {/* Shipping section — Shopee orders only */}
        {isShopeeOrder && (
          <SectionGroup title="Shipping">
            {isShipped || data.shopee_tracking_no ? (
              <div className="grid grid-cols-2 gap-4">
                {data.shopee_shipping_carrier && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Carrier
                    </label>
                    <p className="mt-1">{data.shopee_shipping_carrier}</p>
                  </div>
                )}
                {data.shopee_tracking_no && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Tracking No.
                    </label>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="font-mono text-sm">
                        {data.shopee_tracking_no}
                      </span>
                      <button
                        onClick={handleCopyTracking}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {trackingCopied ? (
                          <Check className="h-3.5 w-3.5 text-green-500" />
                        ) : (
                          <Copy className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Shopee Status
                  </label>
                  <p className="mt-1">{shopeeStatus}</p>
                </div>
                {data.shopee_shipped_at && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Shipped At
                    </label>
                    <p className="mt-1">
                      {new Date(data.shopee_shipped_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  This order is ready to ship.
                </p>
                {isReadyToShip && (
                  <Button
                    variant="link"
                    className="text-primary"
                    onClick={() => setShippingSheetOpen(true)}
                    disabled={inCancellationWindow}
                  >
                    Ship Order
                  </Button>
                )}
              </div>
            )}
          </SectionGroup>
        )}

        {/* Shipping section — non-marketplace orders */}
        {!isShopeeOrder && !data.lazada_order_status && trackingQuery.data?.delivery_note && (
          <SectionGroup title="Shipping">
            {trackingQuery.data.tracking_no ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Tracking No.
                  </label>
                  <div className="mt-1 flex items-center gap-1.5">
                    <span className="font-mono text-sm">
                      {trackingQuery.data.tracking_no}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(trackingQuery.data!.tracking_no!).then(() => {
                          toast.success("Tracking number copied");
                        });
                      }}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {trackingQuery.data.transporter_name && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Carrier
                    </label>
                    <p className="mt-1">{trackingQuery.data.transporter_name}</p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Delivery Note
                  </label>
                  <p className="mt-1 font-mono text-sm">{trackingQuery.data.delivery_note}</p>
                </div>
              </div>
            ) : showTrackingForm ? (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Tracking number"
                      value={trackingInput}
                      onChange={(e) => setTrackingInput(e.target.value)}
                      className="w-full h-10 pl-10 pr-3 text-sm border rounded bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!trackingQuery.data?.delivery_note || !trackingInput.trim()) return;
                      setTrackingMutation.mutate({
                        deliveryNote: trackingQuery.data.delivery_note,
                        trackingNo: trackingInput.trim(),
                      }, {
                        onSuccess: () => {
                          setShowTrackingForm(false);
                          setTrackingInput("");
                        },
                      });
                    }}
                    disabled={!trackingInput.trim() || setTrackingMutation.isPending}
                    className="h-10"
                  >
                    {setTrackingMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setShowTrackingForm(false); setTrackingInput(""); }}
                    className="h-10"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  No tracking number yet.
                </p>
                <Button
                  variant="link"
                  className="text-primary"
                  onClick={() => setShowTrackingForm(true)}
                >
                  Add Tracking No.
                </Button>
              </div>
            )}
          </SectionGroup>
        )}

        {data.items && data.items.length > 0 && (
          <SectionGroup title="Order Items">
            <Card className="glass">
              <CardContent className="p-0">
                <div className="rounded-md border border-border/40 overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Item
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Qty
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Rate
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-muted-foreground">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((item, index) => (
                        <tr
                          key={index}
                          className="border-t border-border/40"
                        >
                          <td className="px-4 py-3 text-sm">
                            {item.item_name || item.item_code}
                          </td>
                          <td className="px-4 py-3 text-sm">{item.qty}</td>
                          <td className="px-4 py-3 text-sm">
                            {item.rate.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium">
                            {item.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </SectionGroup>
        )}
      </DetailView>

      {/* Shopee Shipping Sheet */}
      {isShopeeOrder && (
        <ShopeeShippingSheet
          open={shippingSheetOpen}
          onOpenChange={setShippingSheetOpen}
          orderName={data.name}
          shopeeOrderSn={data.shopee_order_sn!}
        />
      )}
    </PageContainer>
  );
}
